import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoadingController, ToastController } from '@ionic/angular';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class ToolsService {
  private isOpenMenuSubject = new BehaviorSubject<boolean>(true);
  private isMenuFixedSubject = new BehaviorSubject<boolean>(true);
  private isShowMenuSubject = new BehaviorSubject<boolean>(true);
  isDense$ = this.isOpenMenuSubject.asObservable();
  isMenuFixed$ = this.isMenuFixedSubject.asObservable();
  isShowMenu$ = this.isShowMenuSubject.asObservable();
  loading: any;

  private loadingState = new BehaviorSubject<{
    loading: boolean;
    phrase: string;
    gif?: string;
  }>({ loading: false, phrase: '' });
  private progressLoadingModal?: HTMLIonModalElement;
  public progressLoadingChangeText: Subject<string> = new Subject<string>();
  public progressLoadingChangeProgress: Subject<number> = new Subject<number>();
  gifDefault = 'assets/images/loading.gif';

  constructor(
    private toastController: ToastController,
    private loaderController: LoadingController,
    private storageService: StorageService,
    private loadingController: LoadingController
  ) { }

  async loadingPresent(message: string = 'Cargando...') {
    if (this.loading) return;
    this.loading = await this.loaderController.create({
      message: message,
    });
    await this.loading.present();
  }

  async loadingDismiss() {
    await this.loading.dismiss();
    this.loading = null;
  }

  async updateLoadingMessage(message: string) {
    if (!this.loading) return;
    this.loading.message = message;
  }

  setOpenMenu(isDense: boolean) {
    this.isOpenMenuSubject.next(isDense);
  }

  setMenuFixed(isDense: boolean) {
    localStorage.setItem('menuFixed', isDense as any);
    this.isMenuFixedSubject.next(isDense);
  }

  setShowMenu(isDense: boolean) {
    this.isShowMenuSubject.next(isDense);
  }

  async presentErrorToast(message: string, color = 'danger') {
    const toast = await this.toastController.create({
      position: 'top',
      message: message,
      color,
      duration: 8000,
    });
    toast.present();
  }

  async presentSuccefulToast(message: string) {
    const toast = await this.toastController.create({
      position: 'top',
      message: message,
      color: 'success',
      duration: 5000,
    });
    toast.present();
  }

  async presentInfoToast(message: string) {
    const toast = await this.toastController.create({
      position: 'top',
      message: message,
      color: 'primary',
      duration: 5000,
    });
    toast.present();
  }

  async presentConfirmationToast(message: string, onConfirm: () => void) {
    const toast = await this.toastController.create({
      message: message,
      color: 'warning',
      position: 'top',
      buttons: [
        {
          text: 'Sí',
          role: 'confirm',
          handler: onConfirm,
        },
        {
          text: 'No',
          role: 'cancel',
        },
      ],
    });
    await toast.present();
  }

  getAuthHeaderWhitParams(params: any) {
    const headers = this.storageService.getSimpleAuthHeaders();

    let httpParams = new HttpParams();
    Object.keys(params).forEach((key) => {
      if (params[key]) {
        httpParams = httpParams.append(key, params[key]);
      }
    });

    const options = {
      ...headers,
      params: httpParams,
    };

    return options;
  }

  getValueFromBillId(name: string, questions: Array<any>): any {
    let question = questions.find((question) => question.id === name);
    if (question) return question.formControl.value;
    return null;
  }

  getPaymentMethodFormatted(questions: Array<any>): string | null {
    const question = questions.find(q => q.id === 'payment_method');

    if (!question) return null;

    const value = question.formControl.value;

    if (Array.isArray(value) && value.length > 0) {
      return value.join(', ');
    }

    return null;
  }

  async convertObservableToPromise<T>(observable: Observable<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      observable.subscribe({
        next: (data: any) => {
          return resolve(data);
        },
        error: (err: any) => {
          return reject(err);
        },
      });
    });
  }

  getValueFromQuestion(name: string | undefined, questions: Array<any>): any {
    const question = questions.find((question) => question.label === name);
    if (question) {
      const value = question.formControl.value;
      if (question.inputType === 'date') {
        return value?.toString().trim() ? value : ' ';
      }
      return value?.toString().trim() ? value : ' ';
    }
    return null;
  }

  getValueFromIncrementalQuestion(
    id: string,
    questions: Array<any>
  ): { id: string; answer: any } | null {
    const question = questions.find((q) => q.question_id === id);
    if (question) {
      return {
        id: question.question_id,
        answer: question.formControl?.value ?? null,
      };
    }
    return null;
  }

  async showBillErrorAlert(errors: any) {
    let hasErrors = false;
    let notRecognizedFields = '';
    for (let key in errors) {
      if (errors[key]) {
        notRecognizedFields += `<li>${key}</li>`;
        hasErrors = true;
      }
    }
    if (!hasErrors) return;

    // const modal = this.modalCtrl.create({
    //   component: CustomErrorAlertComponent,
    //   cssClass: 'custom-alert-modal',
    //   componentProps: {notRecognizedFields}
    // });

    // (await modal).present();
  }

  get loadingState$() {
    return this.loadingState.asObservable();
  }

  startLoading(initialPhrase: string) {
    this.loadingState.next({
      loading: true,
      phrase: initialPhrase,
      gif: this.gifDefault,
    });
  }

  updateLoadingPhrase(newPhrase: string, gif?: string) {
    this.loadingState.next({
      loading: true,
      phrase: newPhrase,
      gif: gif ? gif : this.gifDefault,
    });
  }

  stopLoading() {
    this.loadingState.next({ loading: false, phrase: '' });
  }

  async showLoading(message: string = 'Cargando...') {
    if (this.loading) return;
    this.loading = await this.loadingController.create({
      message: message,
    });
    await this.loading.present();
  }

  hideLoading() {
    if (!this.loading) return;
    this.loading.dismiss();
    this.loading = null;
  }

  setStartOfDay(date: string | Date): string {
    const d = new Date(date);
    d.setHours(d.getHours() + 5);
    return d.toISOString();
  }

  setEndOfDay(date: string | Date): string {
    const d = new Date(date);
    d.setUTCHours(23, 59, 59, 0);
    d.setHours(d.getHours() + 5);
    return d.toISOString();
  }

  adjustEndDate = (dateStr: string | Date) => {
    const date = new Date(dateStr);
    date.setHours(date.getHours() - 5);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${year}-${month}-${day}`;
  };

  // Method to dynamically add and execute a script in the head
  addScript(htmlScript: string) {
    if (!htmlScript?.trim()) return;
    // Build a fragment safely
    const tpl = document.createElement('template');
    tpl.innerHTML = htmlScript.trim();
    // Mark EVERY element in the fragment so we can remove it later
    tpl.content.querySelectorAll<HTMLElement>('*').forEach(el => {
      el.classList.add('injectedCustomScript');
    });
    // Append to HEAD
    document.head.appendChild(tpl.content);
    // Re-execute scripts:
    //  - any element with .customScript (tu caso actual)
    //  - any <script> that we just injected (marcados con .injectedCustomScript)
    const nodesToProcess = document.querySelectorAll<HTMLElement>(
      '.customScript, script.injectedCustomScript, .injectedCustomScript script'
    );
    this.replaceScriptsInNodes(nodesToProcess);
  }

  // Replace script tags within the provided nodes
  replaceScriptsInNodes(nodes: NodeListOf<HTMLElement> | HTMLElement[]) {
    nodes.forEach((node: HTMLElement) => {
      if (node.tagName === 'SCRIPT') {
        node.replaceWith(this.cloneScript(node as HTMLScriptElement));
      }
    });
  }

  // Clone the script tag to ensure it executes again
  cloneScript(node: HTMLScriptElement) {
    const script = document.createElement('script');
    script.textContent = node.textContent;
    // Copy attributes
    Array.from(node.attributes).forEach(attr => {
      script.setAttribute(attr.name, attr.value);
    });
    // Add a class to mark it as injected
    script.classList.add('injectedCustomScript');
    return script;
  }

  // ✅ Remove ALL injected elements (any tag) that we previously marked
  removeInjectedScripts() {
    const injected = document.querySelectorAll<HTMLElement>('.injectedCustomScript');
    injected.forEach(el => el.remove());
  }

  /* (Opcional) Si quieres solo limpiar el <head>:
  removeInjectedScriptsFromHead() {
    const injected = document.head.querySelectorAll<HTMLElement>('.injectedCustomScript');
    injected.forEach(el => el.remove());
  }
  */

}
